import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "./button";
import { Loader2, UploadCloud, Image as ImageIcon, Trash2 } from "lucide-react";

interface ImageUploadProps {
  value?: string | null;
  onChange: (url: string) => void;
  onRemove?: () => void;
  allowDelete?: boolean;
  className?: string;
  category?: "avatar" | "figure" | "document" | "other";
  maxSizeMB?: number;
}

export function ImageUpload({
  value,
  onChange,
  onRemove,
  allowDelete = true,
  className = "",
  category = "avatar",
  maxSizeMB = 5,
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getUploadUrl = trpc.media.getUploadUrl.useMutation();
  const confirmUpload = trpc.media.confirmUpload.useMutation();
  const deleteMedia = trpc.media.deleteMedia.useMutation();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Only image files are allowed");
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      // 1. Get presigned URL
      const { url, objectKey, publicUrl } = await getUploadUrl.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category,
      });

      // 2. Upload directly to R2
      const uploadRes = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error("Failed to upload file to storage.");
      }

      // 3. Confirm upload
      await confirmUpload.mutateAsync({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        category,
        objectKey,
      });

      onChange(publicUrl);
    } catch (err: any) {
      setError(err.message || "An error occurred during upload");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = async () => {
    if (!value) return;
    const confirmed = window.confirm(
      "Are you sure you want to remove this image? This action will permanently delete it from your cloud storage and site."
    );
    if (!confirmed) return;

    try {
      setIsDeleting(true);
      setError(null);
      if (allowDelete) {
        await deleteMedia.mutateAsync({ url: value });
      }
      onChange("");
      onRemove?.();
    } catch (err: any) {
      setError(err.message || "Failed to remove image from storage");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex items-center gap-4">
        {value ? (
          <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-200 shrink-0 bg-muted/30">
            <img src={value} alt="Uploaded preview" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-16 h-16 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 shrink-0">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          {value ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isDeleting}
                className="w-fit h-7 text-xs"
              >
                {isUploading ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
                )}
                {isUploading ? "Uploading..." : "Change Image"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemove}
                disabled={isUploading || isDeleting}
                className="w-fit h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                title="Remove and permanently delete this image"
              >
                {isDeleting ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                )}
                {isDeleting ? "Deleting..." : "Remove"}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isDeleting}
              className="w-fit h-7 text-xs"
            >
              {isUploading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
              )}
              {isUploading ? "Uploading..." : "Upload Image"}
            </Button>
          )}
          <p className="text-[11px] text-muted-foreground">
            JPG, PNG, WebP up to {maxSizeMB}MB
          </p>
        </div>
      </div>
      {error && <p className="text-xs text-destructive font-medium">{error}</p>}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}

